import { Mutation } from "@apollo/client/react/components";
import React, { useState, ChangeEvent } from "react";
import * as palette from "@govlaunch/palette";
import mediaQueries from "~/lib/utils/mediaQueries";
import { Margin } from "~/components/spacings";
import { Input, Button } from "@govlaunch/core";
import { IGroupNewsworthyItem } from "~/types/types";
import { RelatedUrlCard } from "~/components/newsworthy/RelatedUrlCard";
import NoResultsFound from "~/components/group/NoResultsFound";
import { NoNewsworthy } from "~/components/profile/EmptyBlocks";
import {
  ISetGroupNewsworthyMutation,
  ISetGroupNewsworthyMutationVariables,
} from "~/lib/mutations/__generated__/SetGroupNewsworthy.generated";
import SetGroupNewsworthy from "~/lib/mutations/SetGroupNewsworthy";
import { useToggle } from "react-use";
import { IGroupQuery } from "~/lib/queries/__generated__/GroupQuery.generated";
import { refetchQueries } from "~/lib/apollo/RefetchQueriesFixLink";
import { NewsworthyInput, DuplicatedLinkModal } from "~/components/form/NewsworthyField";
import { useIntl, FormattedMessage } from "react-intl";

export default function Newsworthy({
  newsworthy,
  group,
}: {
  newsworthy: IGroupNewsworthyItem[];
  group: NonNullable<IGroupQuery["groupBySlug"]>;
}) {
  const intl = useIntl();
  const [isEditing, setEditing] = useToggle(false);
  const [filter, setFilter] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const filtered = newsworthy.filter((newsworthyItem) => {
    if (!filter || !newsworthyItem.title) {
      return true;
    }

    return newsworthyItem.title.toLowerCase().includes(filter.toLowerCase());
  });

  if (newsworthy.length === 0) {
    return (
      <Mutation<ISetGroupNewsworthyMutation, ISetGroupNewsworthyMutationVariables>
        mutation={SetGroupNewsworthy}
        refetchQueries={refetchQueries(["Group"])}
      >
        {(setGroupNewsworthy) => (
          <NoNewsworthy
            onAddNewsworthy={(newsworthy) => {
              setGroupNewsworthy({
                variables: {
                  id: group._id,
                  newsworthy: [newsworthy].concat(group.newsworthy).map((item) => ({
                    addedAt: item.addedAt || Date.now(),
                    description: item.description,
                    title: item.title,
                    favicon: item.favicon,
                    siteName: item.siteName,
                    url: item.url,
                  })),
                },
              });
            }}
          />
        )}
      </Mutation>
    );
  }

  return (
    <Mutation<ISetGroupNewsworthyMutation, ISetGroupNewsworthyMutationVariables>
      mutation={SetGroupNewsworthy}
      refetchQueries={refetchQueries(["Group"])}
    >
      {(setGroupNewsworthy) => (
        <div>
          <DuplicatedLinkModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            text={intl.formatMessage({
              defaultMessage: "This link has already been added, please try a different one!",
              id: "HwqbKy",
            })}
            closeButtonText={intl.formatMessage({
              defaultMessage: "Close",
              id: "rbrahO",
            })}
          />
          <div>
            {isEditing && group.viewerIsMember && (
              <Margin mb={16}>
                <NewsworthyInput
                  placeholder={intl.formatMessage({
                    defaultMessage: "Add a news article link and press Enter... ",
                    id: "UUWLXe",
                  })}
                  padding={0}
                  onChange={(newsworthy: any) => {
                    const duplicatedUrl = group.newsworthy.find((item) => item.url === newsworthy.url);

                    if (duplicatedUrl) {
                      setIsModalOpen(true);
                      return;
                    }

                    setGroupNewsworthy({
                      variables: {
                        id: group._id,
                        newsworthy: [newsworthy].concat(group.newsworthy).map((item) => ({
                          addedAt: item.addedAt || Date.now(),
                          description: item.description,
                          title: item.title,
                          favicon: item.favicon,
                          siteName: item.siteName,
                          url: item.url,
                        })),
                      },
                    });
                  }}
                />
              </Margin>
            )}
          </div>

          <div
            data-intercom-target="Group Newsworthy"
            css={{
              borderRadius: 5,
              border: `1px solid ${palette.lightestGray}`,
              background: "#fff",
            }}
          >
            <div
              css={{
                padding: 16,
              }}
            >
              {group.viewerIsMember && (
                <Margin
                  mb={16}
                  css={{
                    display: "flex",
                  }}
                >
                  <Input
                    placeholder={intl.formatMessage({
                      defaultMessage: "Search for links",
                      id: "UFr4XP",
                    })}
                    value={filter}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setFilter(e.target.value)}
                  />

                  <Button
                    theme="link"
                    color={palette.innovatorBlue}
                    textColor={palette.innovatorBlue}
                    onClick={setEditing}
                    css={{
                      color: palette.innovatorBlue,
                      marginLeft: 16,
                      textTransform: "uppercase",
                    }}
                  >
                    <FormattedMessage defaultMessage="Add" id="2/2yg+" />
                  </Button>
                </Margin>
              )}

              <div
                css={mediaQueries({
                  display: "grid",
                  gridTemplateColumns: ["1fr", "1fr", "1fr 1fr"],
                  gridColumnGap: 16,
                  gridRowGap: 16,
                })}
              >
                {filtered.length === 0 && filter.length > 0 && <NoResultsFound />}

                {filtered.map((newsworthyItem, index) => (
                  <RelatedUrlCard
                    onRemove={
                      group.viewerIsMember
                        ? () =>
                            setGroupNewsworthy({
                              variables: {
                                id: group._id,
                                newsworthy: group.newsworthy
                                  .filter(({ url: maybeUrl }) => maybeUrl !== newsworthyItem.url)
                                  .map((item) => ({
                                    addedAt: item.addedAt || Date.now(),
                                    description: item.description,
                                    title: item.title,
                                    favicon: item.favicon,
                                    siteName: item.siteName,
                                    url: item.url,
                                  })),
                              },
                            })
                        : undefined
                    }
                    description={newsworthyItem.description!}
                    title={newsworthyItem.title!}
                    url={newsworthyItem.url!}
                    siteName={newsworthyItem.siteName!}
                    favicon={newsworthyItem.favicon!}
                    key={`${newsworthyItem.url!}_${index}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </Mutation>
  );
}
